var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import { html, LitElement } from 'lit';
import { property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { debounce, isNil, noop } from 'lodash-es';
import { sdsCustomElement } from '../utilities/custom-components';
import { appendStylesToTheStyleRoot, calcLeftOffset, documentDirection, fastDom, pxToNum, } from '../utilities/dom';
import stylesGlobal from './_sds-tree-global.lit.scss';
import { SdsTreeHeadingNode, } from './node/sds-tree-heading-node.lit';
import { SdsTreeNode } from './node/sds-tree-node.lit';
import styles from './sds-tree.lit.scss';
import { ColumnSizingService } from './services/column-sizing-service';
import { HierarchicalTreeStateService, } from './services/hierarchical-tree-state-service';
import { TreeStateService } from './services/tree-state-service';
import { getMaxFirstColumnSize } from './node/utilities';
/**
 * Component for displaying hierarchical data in a tree format with features such as column resizing,
 * node expansion/collapse, and the ability to freeze columns from the left-hand side.
 *
 * @fires sds.tree.column-resize - Fired when a column is resized. Event is of CustomEvent type and contains the following details: `{ columnSizes: string[] }`
 *
 * @slot heading-node - Slot where the heading must be plugged.
 * @slot nodes - Slot where nodes must be plugged.
 *
 * @tag sds-tree
 * @example
 * ```html
 * <sds-tree>
 *    <sds-tree-heading-node>
 *       <sds-tree-heading-cell></sds-tree-heading-cell>
 *    </sds-tree-heading-node>
 *    <sds-tree-node>
 *       <sds-tree-cell></sds-tree-cell>
 *       <sds-tree-node>
 *          <sds-tree-cell></sds-tree-cell>
 *       </sds-tree-node>
 *    </sds-tree-node>
 * </sds-tree>
 * ```
 */
let SdsTree = (() => {
    var _SdsTree_columns_accessor_storage, _SdsTree_gridWidth_accessor_storage, _SdsTree_frozenColumns_accessor_storage, _SdsTree_frozenHeader_accessor_storage, _SdsTree_frozenFooter_accessor_storage, _SdsTree_nonResizable_accessor_storage, _SdsTree_slotStyles_accessor_storage, _SdsTree_slotEmptyStyles_accessor_storage, _SdsTree__resizeBorder_accessor_storage, _SdsTree_childTreeNodes_accessor_storage, _SdsTree__headingNode_accessor_storage, _SdsTree__footerNode_accessor_storage, _SdsTree_gridDivUnchecked_accessor_storage, _SdsTree_nodeContainer_accessor_storage;
    let _classDecorators = [sdsCustomElement('sds-tree')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = LitElement;
    let _instanceExtraInitializers = [];
    let _columns_decorators;
    let _columns_initializers = [];
    let _columns_extraInitializers = [];
    let _gridWidth_decorators;
    let _gridWidth_initializers = [];
    let _gridWidth_extraInitializers = [];
    let _frozenColumns_decorators;
    let _frozenColumns_initializers = [];
    let _frozenColumns_extraInitializers = [];
    let _frozenHeader_decorators;
    let _frozenHeader_initializers = [];
    let _frozenHeader_extraInitializers = [];
    let _frozenFooter_decorators;
    let _frozenFooter_initializers = [];
    let _frozenFooter_extraInitializers = [];
    let _nonResizable_decorators;
    let _nonResizable_initializers = [];
    let _nonResizable_extraInitializers = [];
    let _slotStyles_decorators;
    let _slotStyles_initializers = [];
    let _slotStyles_extraInitializers = [];
    let _slotEmptyStyles_decorators;
    let _slotEmptyStyles_initializers = [];
    let _slotEmptyStyles_extraInitializers = [];
    let __resizeBorder_decorators;
    let __resizeBorder_initializers = [];
    let __resizeBorder_extraInitializers = [];
    let _childTreeNodes_decorators;
    let _childTreeNodes_initializers = [];
    let _childTreeNodes_extraInitializers = [];
    let __headingNode_decorators;
    let __headingNode_initializers = [];
    let __headingNode_extraInitializers = [];
    let __footerNode_decorators;
    let __footerNode_initializers = [];
    let __footerNode_extraInitializers = [];
    let _gridDivUnchecked_decorators;
    let _gridDivUnchecked_initializers = [];
    let _gridDivUnchecked_extraInitializers = [];
    let _nodeContainer_decorators;
    let _nodeContainer_initializers = [];
    let _nodeContainer_extraInitializers = [];
    let _set_columnSizes_decorators;
    let _set_columnMinSizes_decorators;
    let _set_columnMaxSizes_decorators;
    var SdsTree = _classThis = class extends _classSuper {
        /**
         * Number of columns in the tree.
         */
        get columns() { return __classPrivateFieldGet(this, _SdsTree_columns_accessor_storage, "f"); }
        set columns(value) { __classPrivateFieldSet(this, _SdsTree_columns_accessor_storage, value, "f"); }
        /**
         * Width of the tree container.
         */
        get gridWidth() { return __classPrivateFieldGet(this, _SdsTree_gridWidth_accessor_storage, "f"); }
        set gridWidth(value) { __classPrivateFieldSet(this, _SdsTree_gridWidth_accessor_storage, value, "f"); }
        /**
         * Number of columns to freeze from the beginning.
         *
         * **Note:** Due to [Chrome Bug](https://bugs.chromium.org/p/chromium/issues/detail?id=1140374) this feature does not work in Chrome for rtl.
         */
        get frozenColumns() { return __classPrivateFieldGet(this, _SdsTree_frozenColumns_accessor_storage, "f"); }
        set frozenColumns(value) { __classPrivateFieldSet(this, _SdsTree_frozenColumns_accessor_storage, value, "f"); }
        get frozenHeader() { return __classPrivateFieldGet(this, _SdsTree_frozenHeader_accessor_storage, "f"); }
        set frozenHeader(value) { __classPrivateFieldSet(this, _SdsTree_frozenHeader_accessor_storage, value, "f"); }
        get frozenFooter() { return __classPrivateFieldGet(this, _SdsTree_frozenFooter_accessor_storage, "f"); }
        set frozenFooter(value) { __classPrivateFieldSet(this, _SdsTree_frozenFooter_accessor_storage, value, "f"); }
        /**
         * When true, disables manual column resizing. The first column can still be resized through expand/collapse functionality.
         */
        get nonResizable() { return __classPrivateFieldGet(this, _SdsTree_nonResizable_accessor_storage, "f"); }
        set nonResizable(value) { __classPrivateFieldSet(this, _SdsTree_nonResizable_accessor_storage, value, "f"); }
        get slotStyles() { return __classPrivateFieldGet(this, _SdsTree_slotStyles_accessor_storage, "f"); }
        set slotStyles(value) { __classPrivateFieldSet(this, _SdsTree_slotStyles_accessor_storage, value, "f"); }
        get slotEmptyStyles() { return __classPrivateFieldGet(this, _SdsTree_slotEmptyStyles_accessor_storage, "f"); }
        set slotEmptyStyles(value) { __classPrivateFieldSet(this, _SdsTree_slotEmptyStyles_accessor_storage, value, "f"); }
        get _resizeBorder() { return __classPrivateFieldGet(this, _SdsTree__resizeBorder_accessor_storage, "f"); }
        set _resizeBorder(value) { __classPrivateFieldSet(this, _SdsTree__resizeBorder_accessor_storage, value, "f"); }
        get childTreeNodes() { return __classPrivateFieldGet(this, _SdsTree_childTreeNodes_accessor_storage, "f"); }
        set childTreeNodes(value) { __classPrivateFieldSet(this, _SdsTree_childTreeNodes_accessor_storage, value, "f"); }
        get _headingNode() { return __classPrivateFieldGet(this, _SdsTree__headingNode_accessor_storage, "f"); }
        set _headingNode(value) { __classPrivateFieldSet(this, _SdsTree__headingNode_accessor_storage, value, "f"); }
        get _footerNode() { return __classPrivateFieldGet(this, _SdsTree__footerNode_accessor_storage, "f"); }
        set _footerNode(value) { __classPrivateFieldSet(this, _SdsTree__footerNode_accessor_storage, value, "f"); }
        get gridDivUnchecked() { return __classPrivateFieldGet(this, _SdsTree_gridDivUnchecked_accessor_storage, "f"); }
        set gridDivUnchecked(value) { __classPrivateFieldSet(this, _SdsTree_gridDivUnchecked_accessor_storage, value, "f"); }
        get nodeContainer() { return __classPrivateFieldGet(this, _SdsTree_nodeContainer_accessor_storage, "f"); }
        set nodeContainer(value) { __classPrivateFieldSet(this, _SdsTree_nodeContainer_accessor_storage, value, "f"); }
        registerNodeAtLevel(node, level) {
            let bucket = this.nodesByLevel.get(level);
            if (bucket === undefined) {
                bucket = new Set();
                this.nodesByLevel.set(level, bucket);
            }
            bucket.add(node);
        }
        unregisterNodeAtLevel(node, level) {
            const bucket = this.nodesByLevel.get(level);
            if (bucket !== undefined) {
                bucket.delete(node);
                if (bucket.size === 0) {
                    this.nodesByLevel.delete(level);
                }
            }
        }
        getNodesAtLevel(level) {
            const bucket = this.nodesByLevel.get(level);
            return bucket === undefined ? [] : Array.from(bucket);
        }
        constructor() {
            super();
            /**
             * Represents a shared state for a tree that is used to communicate between the tree and its nodes.
             */
            this.state = __runInitializers(this, _instanceExtraInitializers);
            _SdsTree_columns_accessor_storage.set(this, __runInitializers(this, _columns_initializers, 0));
            _SdsTree_gridWidth_accessor_storage.set(this, (__runInitializers(this, _columns_extraInitializers), __runInitializers(this, _gridWidth_initializers, 0)));
            _SdsTree_frozenColumns_accessor_storage.set(this, (__runInitializers(this, _gridWidth_extraInitializers), __runInitializers(this, _frozenColumns_initializers, 0)));
            _SdsTree_frozenHeader_accessor_storage.set(this, (__runInitializers(this, _frozenColumns_extraInitializers), __runInitializers(this, _frozenHeader_initializers, false)));
            _SdsTree_frozenFooter_accessor_storage.set(this, (__runInitializers(this, _frozenHeader_extraInitializers), __runInitializers(this, _frozenFooter_initializers, false)));
            _SdsTree_nonResizable_accessor_storage.set(this, (__runInitializers(this, _frozenFooter_extraInitializers), __runInitializers(this, _nonResizable_initializers, false)));
            _SdsTree_slotStyles_accessor_storage.set(this, (__runInitializers(this, _nonResizable_extraInitializers), __runInitializers(this, _slotStyles_initializers, {
                display: 'inherit',
                'grid-template-columns': 'inherit',
                'grid-gap': 'inherit',
                'grid-column': `1/${this.columns + 4}`,
                width: '100%',
            })));
            _SdsTree_slotEmptyStyles_accessor_storage.set(this, (__runInitializers(this, _slotStyles_extraInitializers), __runInitializers(this, _slotEmptyStyles_initializers, {
                'grid-column': '1 / -1',
                display: 'inherit',
                position: 'sticky',
                inset: 0,
                width: 'unset',
            })));
            _SdsTree__resizeBorder_accessor_storage.set(this, (__runInitializers(this, _slotEmptyStyles_extraInitializers), __runInitializers(this, __resizeBorder_initializers, void 0)));
            this.resolveReady = __runInitializers(this, __resizeBorder_extraInitializers);
            /**
             * Promise that resolves when the tree is ready.
             */
            this.nodeIsReady = new Promise(resolve => {
                this.resolveReady = resolve;
            });
            this.firstUpdateReceived = false;
            _SdsTree_childTreeNodes_accessor_storage.set(this, __runInitializers(this, _childTreeNodes_initializers, void 0));
            _SdsTree__headingNode_accessor_storage.set(this, (__runInitializers(this, _childTreeNodes_extraInitializers), __runInitializers(this, __headingNode_initializers, void 0)));
            _SdsTree__footerNode_accessor_storage.set(this, (__runInitializers(this, __headingNode_extraInitializers), __runInitializers(this, __footerNode_initializers, void 0)));
            _SdsTree_gridDivUnchecked_accessor_storage.set(this, (__runInitializers(this, __footerNode_extraInitializers), __runInitializers(this, _gridDivUnchecked_initializers, void 0)));
            _SdsTree_nodeContainer_accessor_storage.set(this, (__runInitializers(this, _gridDivUnchecked_extraInitializers), __runInitializers(this, _nodeContainer_initializers, void 0)));
            this.lastValidColumnSizes = (__runInitializers(this, _nodeContainer_extraInitializers), []);
            this.nodesByLevel = new Map();
            this.getColumnSizes = () => {
                let width = 0;
                if (this.gridDivUnchecked) {
                    const style = getComputedStyle(this.gridDiv);
                    const fullWidth = pxToNum(style.width);
                    width = this.getEffectiveWidth(fullWidth);
                    const gridTemplateColumns = style.gridTemplateColumns;
                    if (gridTemplateColumns && gridTemplateColumns !== 'none') {
                        const columns = gridTemplateColumns.split(' ').slice(3).map(pxToNum);
                        if (columns.length > 0 && columns.every(size => size > 0)) {
                            this.lastValidColumnSizes = [...columns];
                            return { width, columns };
                        }
                    }
                }
                return { width, columns: this.lastValidColumnSizes };
            };
            this.columnSizingService = new ColumnSizingService(this.getColumnSizes);
            this.handleScroll = () => {
                void fastDom
                    .measure(() => {
                    if (this.frozenColumns > 0) {
                        this.addOrRemoveBordersOnScroll();
                    }
                })
                    .then(() => fastDom.mutate(() => {
                    this.addOrRemoveHeaderBordersOnScroll();
                }))
                    .catch(e => console.error(e))
                    .then();
            };
            this.handleDirChange = (mutations) => {
                for (const mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'dir') {
                        void this.freezeHeaderColumns()
                            .catch(e => console.error(e))
                            .then();
                        this.state.publish('documentDirection', documentDirection());
                    }
                }
            };
            this.handleResize = () => {
                void this.freezeHeaderColumns()
                    .catch(e => console.error(e))
                    .then();
            };
            this.rtlObserver = new MutationObserver(this.handleDirChange);
            this.handleColumnResize = (downEvnt) => {
                if (!(downEvnt.detail.cellIndexes[0] === -1 && downEvnt.detail.borderPlacement === 'left')) {
                    const moveHandler = (moveEvnt) => {
                        this.columnSizingService.resizeColumns(downEvnt.detail.cellIndexes, moveEvnt.pageX - downEvnt.detail.x, document.dir === 'rtl' ? 'rtl' : 'ltr');
                        this.requestUpdate('columnSizes');
                        this.positionColumnResizeBorder(downEvnt.detail.targetCell, downEvnt.detail.borderPlacement);
                    };
                    this.startColumnResize(downEvnt.detail.targetCell, downEvnt.detail.borderPlacement, moveHandler);
                    window.addEventListener('mouseup', upEvnt => {
                        this.columnSizingService.resizeColumns(downEvnt.detail.cellIndexes, upEvnt.pageX - downEvnt.detail.x, document.dir === 'rtl' ? 'rtl' : 'ltr');
                        this.childTreeNodes.forEach(child => {
                            if (child instanceof SdsTreeNode) {
                                child.handleCellResize(downEvnt.detail.cellIndexes);
                            }
                        });
                        this.finishColumnResize(moveHandler);
                        this.requestUpdate('columnSizes');
                    }, { once: true });
                }
            };
            this.handleNodeAddition = () => {
                this.addScrollBorderIfNecessary();
            };
            this.state = new TreeStateService();
            this.initState();
        }
        /**
         * Column sizes in px or %.
         */
        get columnSizes() {
            return this.columnSizingService.getRawSizes().join(' ');
        }
        set columnSizes(value) {
            this.columnSizingService.updateSizes(value, this.columns);
            // Update lastValidColumnSizes when column sizes are explicitly set
            if (value) {
                const sizes = value.split(' ');
                const numericSizes = sizes.map(size => {
                    if (size.endsWith('px')) {
                        return pxToNum(size);
                    }
                    else if (size.endsWith('%') && this.gridDivUnchecked) {
                        const style = getComputedStyle(this.gridDiv);
                        const fullWidth = pxToNum(style.width);
                        const width = this.getEffectiveWidth(fullWidth);
                        return (width * parseFloat(size)) / 100;
                    }
                    else if (size.endsWith('fr')) {
                        // For fr units, use a default size
                        return 120; // Default column width
                    }
                    return 120; // Default column width
                });
                if (numericSizes.length > 0) {
                    this.lastValidColumnSizes = numericSizes;
                }
            }
            this.requestUpdate('columnSizes');
        }
        get calculatedColumnSizes() {
            return this.columnSizingService.getSizes().join(' ');
        }
        /**
         * Minimum column sizes in px.
         * @param sizes
         */
        set columnMinSizes(sizes) {
            this.columnSizingService.updateMinSizes(sizes, this.columns);
            this.requestUpdate('columnMinSizes');
        }
        set columnMaxSizes(sizes) {
            this.columnSizingService.updateMaxSizes(sizes, this.columns);
            this.requestUpdate('columnMaxSizes');
        }
        get headingNode() {
            return this._headingNode && this._headingNode[0];
        }
        get footerNode() {
            return this._footerNode && this._footerNode[0];
        }
        get gridDiv() {
            if (isNil(this.gridDivUnchecked)) {
                throw new Error('Grid div is null');
            }
            else {
                return this.gridDivUnchecked;
            }
        }
        connectedCallback() {
            this.hierarchicalState = new HierarchicalTreeStateService();
            this.nodeId = this.initHierarchicalState();
            super.connectedCallback();
            this.rtlObserver.observe(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            document.getElementsByTagName('html').item(0), { attributeFilter: ['dir'] });
            window.addEventListener('resize', this.handleResize);
            this.addEventListener(SdsTree.EXPAND_ALL, () => {
                void this.expandAll();
            });
            this.addEventListener(SdsTree.COLLAPSE_ALL, () => {
                void this.collapseAll();
            });
            this.hierarchicalState.addNodeAdditionListener(this.handleNodeAddition);
            this.resolveReady();
        }
        disconnectedCallback() {
            var _a;
            super.disconnectedCallback();
            this.rtlObserver.disconnect();
            (_a = this.gridDivUnchecked) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', this.handleScroll);
            window.removeEventListener('resize', this.handleResize);
            this.removeColumnResizeHandler();
            this.state.clearListeners();
            this.hierarchicalState.removeRootNode(this.nodeId);
            this.hierarchicalState.removeNodeAdditionListener(this.handleNodeAddition);
            this.nodeIsReady = new Promise(resolve => {
                this.resolveReady = resolve;
            });
        }
        addEventListener(type, listener, options) {
            super.addEventListener(type, listener, options);
        }
        /**
         * Returns the order of the given node in the view among its siblings.
         * @internal
         * @param {TreeViewNode} node - The node whose order in the view needs to be determined.
         * @returns {number} The index of the given node in the view, or -1 if the node is not found.
         */
        nodeOrderInView(node) {
            return this.childTreeNodes.findIndex(child => child === node);
        }
        render() {
            return html `
            <div
                id="grid-container"
                part="sds-tree"
                class="sds-tree"
                style="grid-template-columns: 0 0 0 ${this
                .calculatedColumnSizes}; grid-template-rows: 1fr;"
            >
                <div id="resize-border" class="sds-tree__resize-border" style="display: none"></div>
                <slot
                    name="heading-node"
                    style="${styleMap(this.slotStyles)}"
                    class="${classMap({
                'sds-tree__header--frozen': this.frozenHeader,
                'sds-tree--non-resizable': this.nonResizable,
            })}"
                    @sds.tree-base-node.data-changed="${(e) => {
                e.preventDefault();
                void this.handleHeaderChange()
                    .catch(e => console.error(e))
                    .then(noop);
            }}"
                ></slot>
                <slot
                    name="nodes"
                    id="node-container"
                    class="sds-tree-nodes"
                    style="${styleMap(this.slotStyles)}"
                ></slot>
                <slot
                    name="empty"
                    @slotchange="${() => {
                this.updateEmptySlotStyles();
                this.requestUpdate();
            }}"
                    style="${styleMap(this.slotEmptyStyles)}"
                ></slot>
                <slot
                    name="footer-node"
                    style="${styleMap(this.slotStyles)}"
                    class="${classMap({
                'sds-tree__footer--frozen': this.frozenFooter,
            })}"
                    @sds.tree-footer-node.data-changed="${(e) => {
                e.preventDefault();
                void this.handleFooterChange()
                    .catch(e => console.error(e))
                    .then(noop);
            }}"
                ></slot>
            </div>
        `;
        }
        async willUpdate(changedProperties) {
            if (changedProperties.has('columns')) {
                this.slotStyles['grid-column'] = `1/${this.columns + 4}`;
                this.state.publish('columnCount', this.columns);
                this.columnSizingService.updateColumnCount(this.columns);
            }
            if (changedProperties.has('frozenColumns')) {
                const changedFrozenColumns = changedProperties.get('frozenColumns');
                await this.updateFrozenColumns(isNil(changedFrozenColumns) ? 0 : changedFrozenColumns);
            }
            if (changedProperties.has('nonResizable')) {
                if (this.nonResizable) {
                    this.removeColumnResizeHandler();
                }
                else {
                    this.registerColumnResizeHandler();
                }
            }
            if (changedProperties.has('frozenHeader')) {
                const newWidth = this.frozenHeader
                    ? this.getEffectiveWidth(this.gridWidth)
                    : this.gridWidth;
                const oldWidth = this.frozenHeader
                    ? this.gridWidth
                    : this.getEffectiveWidth(this.gridWidth);
                this.columnSizingService.calculateColumnNewSizesDuringGridContainerResizing(newWidth, oldWidth);
            }
        }
        updated(changedProperties) {
            if (changedProperties.has('columns')) {
                this.columnSizingService.startResizing();
                this.columnSizingService.finishResizing(false);
                this.requestUpdate();
            }
        }
        async firstUpdated() {
            await this.updateComplete;
            await this.updateColumnsInHeaders();
            if (!this.nonResizable) {
                this.registerColumnResizeHandler();
            }
            appendStylesToTheStyleRoot(this, 'sds-tree', stylesGlobal.cssText);
            await this.freezeHeaderColumns();
            this.gridDiv.addEventListener('scroll', this.handleScroll);
            this.requestUpdate();
            this.firstUpdateReceived = true;
            this.columnSizingService.startResizing();
            this.columnSizingService.finishResizing(false);
            this.observeGridContainerWidth();
            const footerNode = this.querySelector('sds-tree-footer-node');
            footerNode === null || footerNode === void 0 ? void 0 : footerNode.classList.add('sds-tree--horizontal-scrolling');
        }
        getEffectiveWidth(fullWidth) {
            // Account for scrollbar width when header is frozen
            if (this.frozenHeader && this.gridDivUnchecked) {
                const scrollbarWidth = this.gridDivUnchecked.offsetWidth - this.gridDivUnchecked.clientWidth;
                return scrollbarWidth > 0 ? fullWidth - scrollbarWidth : fullWidth;
            }
            return fullWidth;
        }
        observeGridContainerWidth() {
            var _a;
            const gridContainer = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById('grid-container');
            if (gridContainer) {
                const resizeObserverHandler = (entries) => {
                    for (const entry of entries) {
                        if (entry.target == gridContainer && entry.contentRect.width != 0) {
                            const effectiveWidth = this.getEffectiveWidth(entry.contentRect.width);
                            this.columnSizingService.calculateColumnNewSizesDuringGridContainerResizing(effectiveWidth, this.gridWidth ? this.getEffectiveWidth(this.gridWidth) : 0);
                            this.gridWidth = entry.contentRect.width;
                            void this.updateFrozenColumns(this.frozenColumns);
                            this.updateSlotStyles();
                            this.requestUpdate();
                        }
                    }
                };
                const debouncedResizeObserverHandler = debounce(resizeObserverHandler, 500);
                const resizeObserver = new ResizeObserver(debouncedResizeObserverHandler);
                resizeObserver.observe(gridContainer);
            }
        }
        updateSlotStyles() {
            this.slotEmptyStyles = Object.assign(Object.assign({}, this.slotEmptyStyles), { width: `${this.getEffectiveWidth(this.gridWidth)}px` });
        }
        initState() {
            this.state.publish('documentDirection', documentDirection());
        }
        initHierarchicalState() {
            return this.hierarchicalState.addRootNode();
        }
        calculateResizeBorderSize() {
            return `${this.gridDiv.scrollHeight}px`;
        }
        startColumnResize(targetCell, borderPlacement, moveHandler) {
            this._resizeBorder.style.display = 'block';
            this._resizeBorder.style.height = this.calculateResizeBorderSize();
            this.positionColumnResizeBorder(targetCell, borderPlacement);
            this.headingNode && this.headingNode.startColumnResize();
            this.columnSizingService.startResizing();
            this.addEventListener('mousemove', moveHandler);
        }
        finishColumnResize(moveHandler) {
            this._resizeBorder.style.display = 'none';
            this.headingNode && this.headingNode.endColumnResize();
            this.columnSizingService.finishResizing(); // change this when chrome starts supporting subgrids
            // Update all tree nodes after column resize
            this.updateSdsTreeNodesAfterColumnResize(this.childTreeNodes);
            this.dispatchColumnResize();
            this.removeEventListener('mousemove', moveHandler);
        }
        updateSdsTreeNodesAfterColumnResize(nodes) {
            const filteredNodes = nodes.filter(node => node instanceof SdsTreeNode);
            filteredNodes.forEach((node) => {
                node.firstColumnSizeBeforeExpand = parseFloat(this.columnSizingService.getRawSizes()[0]);
                node.firstColumnSizeAfterExpand = parseFloat(this.columnSizingService.getRawSizes()[0]);
                this.updateSdsTreeNodesAfterColumnResize(node.childElements);
            });
        }
        positionColumnResizeBorder(targetCell, borderPlacement) {
            this._resizeBorder.style.left = 'auto';
            if (borderPlacement === 'left') {
                calcLeftOffset(targetCell, this, this.gridDiv).then(left => {
                    const adjustedLeft = pxToNum(left) - 1 - this.gridDiv.scrollLeft;
                    this._resizeBorder.style.left = `${adjustedLeft}px`;
                }, e => console.error(e));
            }
            else {
                calcLeftOffset(targetCell, this, this.gridDiv)
                    .then(left => fastDom.measure(() => {
                    const adjustedRight = targetCell.getBoundingClientRect().width +
                        pxToNum(left) -
                        1 -
                        this.gridDiv.scrollLeft;
                    return `${adjustedRight}px`;
                }))
                    .then(right => (this._resizeBorder.style.left = right), e => console.error(e));
            }
        }
        async handleHeaderChange() {
            this.removeColumnResizeHandler();
            if (!this.nonResizable) {
                this.registerColumnResizeHandler();
            }
            await this.updateColumnsInHeaders();
            await this.freezeHeaderColumns();
        }
        async updateColumnsInHeaders() {
            await fastDom.mutate(() => {
                this.headingNode && this.headingNode.updateColumns();
            });
        }
        registerColumnResizeHandler() {
            if (this.frozenColumns === 0 || this.frozenColumns === 1) {
                this.headingNode &&
                    this.headingNode.addEventListener(SdsTreeHeadingNode.BORDER_MOUSEDOWN_EVENT, this.handleColumnResize);
            }
        }
        removeColumnResizeHandler() {
            this.headingNode &&
                this.headingNode.removeEventListener(SdsTreeHeadingNode.BORDER_MOUSEDOWN_EVENT, this.handleColumnResize);
        }
        async updateFrozenColumns(oldValue) {
            if (oldValue !== 0) {
                await this.unFreezeAllColumns();
            }
            if (this.frozenColumns > 0) {
                await this.freezeHeaderColumns();
                this.gridDiv.addEventListener('scroll', this.handleScroll);
            }
            this.state.publish('frozenColumnCount', this.frozenColumns);
        }
        async unFreezeAllColumns() {
            await fastDom.mutate(() => {
                this.headingNode && this.headingNode.unFreezeAllColumns();
                this.gridDiv.removeEventListener('scroll', this.handleScroll);
            });
        }
        async freezeHeaderColumns() {
            await fastDom.measure(() => {
                this.headingNode && this.headingNode.freezeColumns(this.frozenColumns);
            });
        }
        async handleFooterChange() {
            await this.freezeFooterColumns();
        }
        async freezeFooterColumns() {
            await fastDom.measure(() => {
                this.footerNode && this.footerNode.freezeColumns(this.frozenColumns);
            });
        }
        dispatchColumnResize() {
            this.dispatchEvent(new CustomEvent(SdsTree.COLUMN_RESIZE, {
                bubbles: false,
                composed: false,
                detail: {
                    columnSizes: this.columnSizingService.getRawSizes(),
                    columnMinSizes: this.columnSizingService.getMinSizes(),
                },
            }));
        }
        addOrRemoveBordersOnScroll() {
            if (this.gridDiv.scrollLeft === 0) {
                this.childTreeNodes.forEach(node => node.removeBorderOnScroll(this.frozenColumns));
                this.headingNode && this.headingNode.removeBorderOnScroll(this.frozenColumns);
                this._footerNode.forEach(footer => footer.removeBorderOnScroll(this.frozenColumns));
            }
            else {
                this.childTreeNodes.forEach(node => node.addBorderOnScroll(this.frozenColumns));
                this.headingNode && this.headingNode.addBorderOnScroll(this.frozenColumns);
                this._footerNode.forEach(footer => footer.addBorderOnScroll(this.frozenColumns));
            }
        }
        addOrRemoveHeaderBordersOnScroll() {
            if (this.headingNode) {
                if (this.gridDiv.scrollTop === 0) {
                    this.headingNode.classList.remove('sds-tree--horizontal-scrolling');
                }
                else {
                    this.headingNode.classList.add('sds-tree--horizontal-scrolling');
                }
            }
            const footerNode = this.querySelector('sds-tree-footer-node');
            this.updateEmptySlotStyles();
            if (footerNode) {
                if (this.gridDiv.scrollTop + this.gridDiv.offsetHeight >= this.gridDiv.scrollHeight) {
                    footerNode.classList.remove('sds-tree--horizontal-scrolling');
                }
                else {
                    footerNode.classList.add('sds-tree--horizontal-scrolling');
                }
            }
            this.requestUpdate();
        }
        updateEmptySlotStyles() {
            const footerNode = this.querySelector('sds-tree-footer-node');
            if (footerNode) {
                const emptySlot = this.renderRoot.querySelector('slot[name="empty"]');
                if (emptySlot) {
                    if (emptySlot.assignedNodes().length > 0) {
                        const footerHeight = footerNode.getBoundingClientRect().height;
                        this.slotEmptyStyles['padding-bottom'] = `${footerHeight}px`;
                    }
                    else {
                        this.slotEmptyStyles['padding-bottom'] = '0px';
                    }
                }
            }
        }
        async expandCollapseColumn(newSize) {
            const delta = newSize - parseFloat(this.columnSizingService.getRawSizes()[0]);
            if (delta != 0) {
                return this.triggerColumnResizing(delta);
            }
        }
        async triggerColumnResizing(delta) {
            if (this.nonResizable) {
                return;
            }
            const newSizes = this.columnSizingService.expandCollapseColumn(delta, document.dir === 'rtl' ? 'rtl' : 'ltr');
            this.columnSizes = newSizes.join(' ');
            await this.updateComplete;
            this.columnSizingService.finishResizing();
            return this.updateFrozenColumns(this.frozenColumns);
        }
        addScrollBorderIfNecessary() {
            void fastDom
                .measure(() => {
                if (this.gridDiv.scrollLeft > 0) {
                    this.childTreeNodes.forEach(node => node.addBorderOnScroll(this.frozenColumns));
                    this.headingNode && this.headingNode.addBorderOnScroll(this.frozenColumns);
                }
            })
                .catch(e => console.error(e))
                .then();
        }
        async expandAll() {
            const sdsTreeNodes = this.childTreeNodes.filter((node) => {
                return getComputedStyle(node).display === 'grid' && node instanceof SdsTreeNode;
            });
            await this.expandTreeNodesWithChildren(sdsTreeNodes);
            const firstColumnMaxSize = getMaxFirstColumnSize(sdsTreeNodes);
            return this.expandCollapseColumn(firstColumnMaxSize);
        }
        async expandTreeNodesWithChildren(nodes) {
            var _a, e_1, _b, _c;
            try {
                for (var _d = true, nodes_1 = __asyncValues(nodes), nodes_1_1; nodes_1_1 = await nodes_1.next(), _a = nodes_1_1.done, !_a; _d = true) {
                    _c = nodes_1_1.value;
                    _d = false;
                    const childNode = _c;
                    await childNode.expandWithoutResize();
                    const children = childNode.childElements.filter((element) => {
                        return element instanceof SdsTreeNode;
                    });
                    await this.expandTreeNodesWithChildren(children);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = nodes_1.return)) await _b.call(nodes_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        async collapseAll() {
            const sdsTreeNodes = this.childTreeNodes.filter((node) => {
                return getComputedStyle(node).display === 'grid' && node instanceof SdsTreeNode;
            });
            await this.collapseTreeNodesWithChildren(sdsTreeNodes);
            const firstColumnMaxSize = getMaxFirstColumnSize(sdsTreeNodes);
            return this.expandCollapseColumn(firstColumnMaxSize);
        }
        async collapseTreeNodesWithChildren(nodes) {
            var _a, e_2, _b, _c;
            try {
                for (var _d = true, nodes_2 = __asyncValues(nodes), nodes_2_1; nodes_2_1 = await nodes_2.next(), _a = nodes_2_1.done, !_a; _d = true) {
                    _c = nodes_2_1.value;
                    _d = false;
                    const childNode = _c;
                    await childNode.collapseWithoutResize();
                    const children = childNode.childElements.filter((element) => {
                        return element instanceof SdsTreeNode;
                    });
                    await this.collapseTreeNodesWithChildren(children);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = nodes_2.return)) await _b.call(nodes_2);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    };
    _SdsTree_columns_accessor_storage = new WeakMap();
    _SdsTree_gridWidth_accessor_storage = new WeakMap();
    _SdsTree_frozenColumns_accessor_storage = new WeakMap();
    _SdsTree_frozenHeader_accessor_storage = new WeakMap();
    _SdsTree_frozenFooter_accessor_storage = new WeakMap();
    _SdsTree_nonResizable_accessor_storage = new WeakMap();
    _SdsTree_slotStyles_accessor_storage = new WeakMap();
    _SdsTree_slotEmptyStyles_accessor_storage = new WeakMap();
    _SdsTree__resizeBorder_accessor_storage = new WeakMap();
    _SdsTree_childTreeNodes_accessor_storage = new WeakMap();
    _SdsTree__headingNode_accessor_storage = new WeakMap();
    _SdsTree__footerNode_accessor_storage = new WeakMap();
    _SdsTree_gridDivUnchecked_accessor_storage = new WeakMap();
    _SdsTree_nodeContainer_accessor_storage = new WeakMap();
    __setFunctionName(_classThis, "SdsTree");
    (() => {
        var _a;
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create((_a = _classSuper[Symbol.metadata]) !== null && _a !== void 0 ? _a : null) : void 0;
        _columns_decorators = [property({ type: Number })];
        _gridWidth_decorators = [property({ type: Number })];
        _frozenColumns_decorators = [property({ type: Number, attribute: 'frozen-columns' })];
        _frozenHeader_decorators = [property({ type: Boolean, attribute: 'frozen-header' })];
        _frozenFooter_decorators = [property({ type: Boolean, attribute: 'frozen-footer' })];
        _nonResizable_decorators = [property({ type: Boolean, attribute: 'non-resizable' })];
        _slotStyles_decorators = [state()];
        _slotEmptyStyles_decorators = [state()];
        __resizeBorder_decorators = [query('#resize-border', true)];
        _childTreeNodes_decorators = [queryAssignedElements({
                selector: 'sds-tree-node, sds-tree-load-more-node, sds-tree-total-data-node',
                flatten: true,
                slot: 'nodes',
            })];
        __headingNode_decorators = [queryAssignedElements({
                selector: 'sds-tree-heading-node',
                flatten: true,
                slot: 'heading-node',
            })];
        __footerNode_decorators = [queryAssignedElements({
                selector: 'sds-tree-footer-node',
                flatten: true,
                slot: 'footer-node',
            })];
        _gridDivUnchecked_decorators = [query('#grid-container')];
        _nodeContainer_decorators = [query('#node-container')];
        _set_columnSizes_decorators = [property({ type: String, attribute: 'column-sizes', reflect: true })];
        _set_columnMinSizes_decorators = [property({ type: String, attribute: 'column-min-sizes' })];
        _set_columnMaxSizes_decorators = [property({ type: String, attribute: 'column-max-sizes' })];
        __esDecorate(_classThis, null, _columns_decorators, { kind: "accessor", name: "columns", static: false, private: false, access: { has: obj => "columns" in obj, get: obj => obj.columns, set: (obj, value) => { obj.columns = value; } }, metadata: _metadata }, _columns_initializers, _columns_extraInitializers);
        __esDecorate(_classThis, null, _gridWidth_decorators, { kind: "accessor", name: "gridWidth", static: false, private: false, access: { has: obj => "gridWidth" in obj, get: obj => obj.gridWidth, set: (obj, value) => { obj.gridWidth = value; } }, metadata: _metadata }, _gridWidth_initializers, _gridWidth_extraInitializers);
        __esDecorate(_classThis, null, _frozenColumns_decorators, { kind: "accessor", name: "frozenColumns", static: false, private: false, access: { has: obj => "frozenColumns" in obj, get: obj => obj.frozenColumns, set: (obj, value) => { obj.frozenColumns = value; } }, metadata: _metadata }, _frozenColumns_initializers, _frozenColumns_extraInitializers);
        __esDecorate(_classThis, null, _frozenHeader_decorators, { kind: "accessor", name: "frozenHeader", static: false, private: false, access: { has: obj => "frozenHeader" in obj, get: obj => obj.frozenHeader, set: (obj, value) => { obj.frozenHeader = value; } }, metadata: _metadata }, _frozenHeader_initializers, _frozenHeader_extraInitializers);
        __esDecorate(_classThis, null, _frozenFooter_decorators, { kind: "accessor", name: "frozenFooter", static: false, private: false, access: { has: obj => "frozenFooter" in obj, get: obj => obj.frozenFooter, set: (obj, value) => { obj.frozenFooter = value; } }, metadata: _metadata }, _frozenFooter_initializers, _frozenFooter_extraInitializers);
        __esDecorate(_classThis, null, _nonResizable_decorators, { kind: "accessor", name: "nonResizable", static: false, private: false, access: { has: obj => "nonResizable" in obj, get: obj => obj.nonResizable, set: (obj, value) => { obj.nonResizable = value; } }, metadata: _metadata }, _nonResizable_initializers, _nonResizable_extraInitializers);
        __esDecorate(_classThis, null, _slotStyles_decorators, { kind: "accessor", name: "slotStyles", static: false, private: false, access: { has: obj => "slotStyles" in obj, get: obj => obj.slotStyles, set: (obj, value) => { obj.slotStyles = value; } }, metadata: _metadata }, _slotStyles_initializers, _slotStyles_extraInitializers);
        __esDecorate(_classThis, null, _slotEmptyStyles_decorators, { kind: "accessor", name: "slotEmptyStyles", static: false, private: false, access: { has: obj => "slotEmptyStyles" in obj, get: obj => obj.slotEmptyStyles, set: (obj, value) => { obj.slotEmptyStyles = value; } }, metadata: _metadata }, _slotEmptyStyles_initializers, _slotEmptyStyles_extraInitializers);
        __esDecorate(_classThis, null, __resizeBorder_decorators, { kind: "accessor", name: "_resizeBorder", static: false, private: false, access: { has: obj => "_resizeBorder" in obj, get: obj => obj._resizeBorder, set: (obj, value) => { obj._resizeBorder = value; } }, metadata: _metadata }, __resizeBorder_initializers, __resizeBorder_extraInitializers);
        __esDecorate(_classThis, null, _childTreeNodes_decorators, { kind: "accessor", name: "childTreeNodes", static: false, private: false, access: { has: obj => "childTreeNodes" in obj, get: obj => obj.childTreeNodes, set: (obj, value) => { obj.childTreeNodes = value; } }, metadata: _metadata }, _childTreeNodes_initializers, _childTreeNodes_extraInitializers);
        __esDecorate(_classThis, null, __headingNode_decorators, { kind: "accessor", name: "_headingNode", static: false, private: false, access: { has: obj => "_headingNode" in obj, get: obj => obj._headingNode, set: (obj, value) => { obj._headingNode = value; } }, metadata: _metadata }, __headingNode_initializers, __headingNode_extraInitializers);
        __esDecorate(_classThis, null, __footerNode_decorators, { kind: "accessor", name: "_footerNode", static: false, private: false, access: { has: obj => "_footerNode" in obj, get: obj => obj._footerNode, set: (obj, value) => { obj._footerNode = value; } }, metadata: _metadata }, __footerNode_initializers, __footerNode_extraInitializers);
        __esDecorate(_classThis, null, _gridDivUnchecked_decorators, { kind: "accessor", name: "gridDivUnchecked", static: false, private: false, access: { has: obj => "gridDivUnchecked" in obj, get: obj => obj.gridDivUnchecked, set: (obj, value) => { obj.gridDivUnchecked = value; } }, metadata: _metadata }, _gridDivUnchecked_initializers, _gridDivUnchecked_extraInitializers);
        __esDecorate(_classThis, null, _nodeContainer_decorators, { kind: "accessor", name: "nodeContainer", static: false, private: false, access: { has: obj => "nodeContainer" in obj, get: obj => obj.nodeContainer, set: (obj, value) => { obj.nodeContainer = value; } }, metadata: _metadata }, _nodeContainer_initializers, _nodeContainer_extraInitializers);
        __esDecorate(_classThis, null, _set_columnSizes_decorators, { kind: "setter", name: "columnSizes", static: false, private: false, access: { has: obj => "columnSizes" in obj, set: (obj, value) => { obj.columnSizes = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
        __esDecorate(_classThis, null, _set_columnMinSizes_decorators, { kind: "setter", name: "columnMinSizes", static: false, private: false, access: { has: obj => "columnMinSizes" in obj, set: (obj, value) => { obj.columnMinSizes = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
        __esDecorate(_classThis, null, _set_columnMaxSizes_decorators, { kind: "setter", name: "columnMaxSizes", static: false, private: false, access: { has: obj => "columnMaxSizes" in obj, set: (obj, value) => { obj.columnMaxSizes = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        SdsTree = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
    })();
    _classThis.COLUMN_RESIZE = 'sds.tree.column-resize';
    _classThis.EXPAND_ALL = 'sds.tree.expand-all';
    _classThis.COLLAPSE_ALL = 'sds.tree.collapse-all';
    _classThis.styles = styles;
    (() => {
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return SdsTree = _classThis;
})();
export { SdsTree };
